<template>
  <div class="email-verifier">
    <div class="w-full flex flex-col gap-2">
      <div
        class="h-16 flex items-center justify-between gap-2 border border-rim-300 px-4 py-[10px] rounded-lg hover:rim-400"
      >
        <div class="flex items-center gap-2">
          <SvgIcon name="account-email" :size="26" />
          <div class="flex flex-col justify-between">
            <div class="ink-14 font-medium text-ink-950">
              {{ t('components.email-verify-code-input.label') }}
            </div>
            <div class="ink-14 text-ink-700">
              {{ verifyData.unauthorized ? targetEmail : maskSensitive(targetEmail) }}
            </div>
          </div>
        </div>

        <SendCodeButton
          :send-fn="handleSend"
          :instance-id="resolvedInstanceId"
          target-type="email"
          target-field="email"
        >
          <template #default="{ remainingTime, isLoading, disabled }">
            <a-button
              :disabled="disabled"
              :loading="isLoading"
              :style="{
                borderRadius: '4px',
                height: '26px',
                padding: '0 12px',
                border: 'none',
                backgroundColor: 'var(--blue-primary-bg)',
              }"
              class="ink-12 font-medium text-blue-primary"
            >
              {{
                remainingTime === 0
                  ? t('components.email-verify-code-input.send-code')
                  : t('components.email-verify-code-input.resend-code', { time: remainingTime })
              }}
            </a-button>
          </template>
        </SendCodeButton>
      </div>

      <div class="w-full">
        <VerifyCodeInput @submit="handleSubmit" />
        <div
          :class="verifyResult === true ? 'ink-12 text-success mt-2' : 'ink-12 text-ink-600 mt-2'"
        >
          {{
            verifyResult === true
              ? t('components.email-verify-code-input.hint.pass')
              : t('components.email-verify-code-input.hint.tip')
          }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import i18n from '@/locales'
import { SendCodeButton, VerifyCodeInput, SvgIcon } from '@/components'
import { toRefs, ref, computed } from 'vue'
import { maskSensitive } from '@/utils/mask'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/store'
import type { Condition } from '../types'
import { useVerifierRunner } from '../useVerifierRunner'

const t = (i18n.global as any).t

const props = defineProps<{
  condition: Condition
  passageKey: string
  securityCode: string
  resolvedInstanceId: string
  make: (c: Condition) => Promise<any>
  verifyData: { email: string; phone: string; loginType: number; unauthorized: boolean }
}>()

const emit = defineEmits<{ (e: 'verified', ok: boolean): void }>()
const { resolvedInstanceId } = toRefs(props)

const userStore = useUserStore()
const { userEmail } = storeToRefs(userStore)
const verifyResult = ref<boolean | undefined>(undefined)
const targetEmail = computed(() => {
  return props.verifyData.email || userEmail.value
})

const { send, verify } = useVerifierRunner(
  props.condition,
  props.make,
  props.verifyData.unauthorized,
)

async function handleSend() {
  await send()
}

async function handleSubmit(code: string) {
  const ok = await verify({ code })
  verifyResult.value = ok
  emit('verified', !!ok)
}
</script>
